
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchEmployees } from "../redux/actions/employeeActions";
import { directCheckIn, directCheckOut } from "../redux/actions/attendanceActions";
import Papa from "papaparse";
import {
  FiChevronLeft, FiChevronRight, FiSearch, FiDownload, FiFilter,
  FiUser, FiMail, FiPhone, FiCalendar, FiX, FiMapPin, FiClock,
  FiLogIn, FiLogOut, FiCheckCircle
} from "react-icons/fi";
import DirectAttendanceModal from "../pages/DirectAttendanceModal";

const DirectAttendance = () => {
  const dispatch = useDispatch();
  const { employees, loading } = useSelector((state) => state.employee);
  const { loading: attendanceLoading } = useSelector((state) => state.directAttendance || {});

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedMunicipality, setSelectedMunicipality] = useState("");
  const [selectedWard, setSelectedWard] = useState("");
  const [selectedDesignation, setSelectedDesignation] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceModal, setAttendanceModal] = useState({ open: false, employee: null, type: null });
  const [todaysAttendance, setTodaysAttendance] = useState({});

  const itemsPerPage = 10;

  useEffect(() => {
    dispatch(fetchEmployees());
  }, [dispatch]);

  // Check today's attendance status for each employee
  useEffect(() => {
    const checkTodaysAttendance = () => {
      const attendance = {};
      employees.forEach(emp => {
        const key = `attendance_${emp.id}_${selectedDate}`;
        const saved = localStorage.getItem(key);
        if (saved) {
          attendance[emp.id] = JSON.parse(saved);
        }
      });
      setTodaysAttendance(attendance);
    };
    
    checkTodaysAttendance();
  }, [employees, selectedDate]);

  const districts = [...new Set(employees.map((e) => e.district).filter(Boolean))];
  const municipalities = [...new Set(employees.map((e) => e.municipality).filter(Boolean))];
  const statuses = [...new Set(employees.map((e) => e.status).filter(Boolean))];
  const designations = [...new Set(employees.map((e) => e.designation).filter(Boolean))];

  const sortedEmployees = [...employees].sort((a, b) => {
    if (!sortField) return 0;
    const aValue = a[sortField] || "";
    const bValue = b[sortField] || "";
    if (sortOrder === "asc") return aValue > bValue ? 1 : -1;
    else return aValue < bValue ? 1 : -1;
  });

  const filteredEmployees = sortedEmployees.filter((emp) => {
    const term = searchTerm.toLowerCase();
    return (
      (!selectedDistrict || emp.district === selectedDistrict) &&
      (!selectedMunicipality || emp.municipality === selectedMunicipality) &&
      (!selectedDesignation || emp.designation === selectedDesignation) &&
      (!selectedStatus || emp.status === selectedStatus) &&
      ((emp?.name || "").toLowerCase().includes(term) ||
        (emp?.email || "").toLowerCase().includes(term) ||
        (emp?.phone || "").toLowerCase().includes(term) ||
        (emp?.designation || "").toLowerCase().includes(term))
    );
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const currentItems = filteredEmployees.slice(indexOfLastItem - itemsPerPage, indexOfLastItem);
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleDownloadCSV = () => {
    const csvRows = [
      ["ID", "Employee Name", "Contact", "Position", "Check In", "Check Out", "Date", "Status"],
    ];

    filteredEmployees.forEach((emp) => {
      const attendance = todaysAttendance[emp.id] || {};
      csvRows.push([
        emp.id,
        emp.name || "",
        `${emp.email || ""} | ${emp.phone || ""}`,
        emp.designation || "",
        attendance.check_in || "Not Checked In",
        attendance.check_out || "Not Checked Out",
        selectedDate,
        emp.status || "",
      ]);
    });

    const csv = Papa.unparse(csvRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Attendance_Report_${selectedDate}.csv`;
    link.click();
  };

  const handleDirectAttendance = async (type, employee, data) => {
    try {
      let result;
      if (type === 'checkin') {
        result = await dispatch(directCheckIn({
          user_id: data.user_id,
          check_in: data.check_in,
          date: data.date,
          check_in_latitude: data.check_in_latitude,
          check_in_longitude: data.check_in_longitude
        }));
      } else {
        result = await dispatch(directCheckOut({
          user_id: data.user_id,
          check_out: data.check_out,
          date: data.date,
          check_out_latitude: data.check_out_latitude,
          check_out_longitude: data.check_out_longitude
        }));
      }

      // Save to localStorage for demo purposes
      const key = `attendance_${employee.id}_${selectedDate}`;
      const current = todaysAttendance[employee.id] || {};
      const updated = {
        ...current,
        ...(type === 'checkin' 
          ? { 
              check_in: data.check_in,
              check_in_latitude: data.check_in_latitude,
              check_in_longitude: data.check_in_longitude
            }
          : { 
              check_out: data.check_out,
              check_out_latitude: data.check_out_latitude,
              check_out_longitude: data.check_out_longitude
            }
        )
      };
      
      localStorage.setItem(key, JSON.stringify(updated));
      setTodaysAttendance(prev => ({
        ...prev,
        [employee.id]: updated
      }));

      setAttendanceModal({ open: false, employee: null, type: null });
    } catch (error) {
      console.error(`Error with ${type}:`, error);
    }
  };

  const clearFilters = () => {
    setSelectedDistrict("");
    setSelectedMunicipality("");
    setSelectedDesignation("");
    setSelectedStatus("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const getAttendanceStatus = (empId) => {
    const att = todaysAttendance[empId];
    if (!att) return 'none';
    if (att.check_in && !att.check_out) return 'checked_in';
    if (att.check_in && att.check_out) return 'completed';
    return 'none';
  };

  const getStatusBadgeColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'inactive': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
      case 'on leave': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      default: return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    }
  };

  const columns = [
    { key: "photo", label: "Photo", width: "60px" },
    { key: "name", label: "Employee Details", width: "200px" },
    { key: "contact", label: "Contact", width: "180px" },
    { key: "position", label: "Position", width: "150px" },
    { key: "attendance", label: "Attendance", width: "200px" },
    { key: "status", label: "Status", width: "100px" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Direct Attendance Management</h1>
          <p className="text-gray-600 dark:text-gray-400">Mark attendance manually for employees</p>
        </div>
        <div className="flex items-center gap-3 mt-4 lg:mt-0">
          <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700">
            <FiCalendar className="text-gray-400" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-transparent border-none focus:outline-none text-gray-700 dark:text-gray-300"
            />
          </div>
          <button 
            onClick={handleDownloadCSV} 
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl transition-all shadow-sm"
          >
            <FiDownload /> Export Report
          </button>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, contact, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700 border-none rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white transition-all"
            />
          </div>
          <button 
            onClick={() => setShowFilters(!showFilters)} 
            className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 px-4 py-2.5 rounded-xl text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
          >
            <FiFilter /> Filters
          </button>
          {(selectedDesignation || selectedStatus || searchTerm) && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-4 py-2.5 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-all"
            >
              <FiX /> Clear
            </button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-5 pt-5 border-t border-gray-100 dark:border-gray-700">
            <select 
              value={selectedDesignation} 
              onChange={(e) => setSelectedDesignation(e.target.value)} 
              className="bg-gray-50 dark:bg-gray-700 border-none rounded-lg p-2.5 text-sm dark:text-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Designations</option>
              {designations.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select 
              value={selectedStatus} 
              onChange={(e) => setSelectedStatus(e.target.value)} 
              className="bg-gray-50 dark:bg-gray-700 border-none rounded-lg p-2.5 text-sm dark:text-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              {statuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Main Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 dark:bg-gray-700/30 border-b border-gray-100 dark:border-gray-700">
                {columns.map((col) => (
                  <th 
                    key={col.key} 
                    className="p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-500 transition-colors"
                    style={{ width: col.width }}
                    onClick={() => !['photo', 'attendance'].includes(col.key) && toggleSort(col.key)}
                  >
                    <div className="flex items-center gap-2">
                      {col.label}
                      {sortField === col.key && (
                        <span className="text-blue-500">{sortOrder === "asc" ? "↑" : "↓"}</span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {currentItems.map((emp) => {
                const attendanceStatus = getAttendanceStatus(emp.id);
                const attendance = todaysAttendance[emp.id] || {};
                
                return (
                  <tr key={emp.id} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/20 transition-colors">
                    {/* Photo Column */}
                    <td className="p-4">
                      <img
                        src={emp.photo || "https://img.freepik.com/free-vector/blue-circle-with-white-user_78370-4707.jpg"}
                        className="h-12 w-12 rounded-full object-cover ring-2 ring-gray-100 dark:ring-gray-700"
                        alt={emp.name}
                      />
                    </td>

                    {/* Employee Details */}
                    <td className="p-4">
                      <div className="font-bold text-gray-900 dark:text-white">{emp.name}</div>
                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                        <FiMapPin className="text-gray-400" /> {emp.district || "No Location"}
                      </div>
                      <div className="text-[10px] text-gray-400 uppercase mt-0.5">ID: {emp.id}</div>
                    </td>

                    {/* Contact Info */}
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <FiMail className="text-blue-400 shrink-0" /> {emp.email}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                        <FiPhone className="text-green-400 shrink-0" /> {emp.phone}
                      </div>
                    </td>

                    {/* Position */}
                    <td className="p-4">
                      <div className="inline-block px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 text-xs font-bold mb-1">
                        {emp.designation}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {emp.office_name || "Head Office"}
                      </div>
                    </td>

                    {/* Attendance Column */}
                    <td className="p-4">
                      <div className="space-y-2">
                        {/* Check In Status */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-500">Check In:</span>
                          {attendance.check_in ? (
                            <div className="flex items-center gap-1 text-green-600">
                              <FiCheckCircle className="text-green-500" size={14} />
                              <span className="text-sm font-medium">{attendance.check_in}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </div>
                        
                        {/* Check Out Status */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-500">Check Out:</span>
                          {attendance.check_out ? (
                            <div className="flex items-center gap-1 text-orange-600">
                              <FiCheckCircle className="text-orange-500" size={14} />
                              <span className="text-sm font-medium">{attendance.check_out}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </div>

                        {/* Quick Action Buttons */}
                        <div className="flex gap-1 mt-2">
                          {attendanceStatus !== 'completed' && (
                            <>
                              {attendanceStatus !== 'checked_in' && (
                                <button
                                  onClick={() => setAttendanceModal({ open: true, employee: emp, type: 'checkin' })}
                                  disabled={attendanceLoading}
                                  className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-green-50 hover:bg-green-100 text-green-600 rounded-md text-xs font-medium transition-all disabled:opacity-50"
                                >
                                  <FiLogIn size={12} />
                                  Check In
                                </button>
                              )}
                              {attendanceStatus === 'checked_in' && (
                                <button
                                  onClick={() => setAttendanceModal({ open: true, employee: emp, type: 'checkout' })}
                                  disabled={attendanceLoading}
                                  className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-orange-50 hover:bg-orange-100 text-orange-600 rounded-md text-xs font-medium transition-all disabled:opacity-50"
                                >
                                  <FiLogOut size={12} />
                                  Check Out
                                </button>
                              )}
                            </>
                          )}
                          {attendanceStatus === 'completed' && (
                            <span className="flex-1 text-center px-2 py-1 bg-gray-100 text-gray-600 rounded-md text-xs font-medium">
                              Completed
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Status Column */}
                    <td className="p-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(emp.status)}`}>
                        {emp.status || 'Active'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {currentItems.length === 0 && (
          <div className="p-20 text-center text-gray-400">
            <FiUser size={48} className="mx-auto mb-4 opacity-20" />
            <p className="text-lg">No records match your criteria</p>
            <button
              onClick={clearFilters}
              className="mt-4 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-all"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 px-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 disabled:opacity-30 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
          >
            <FiChevronLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={i}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-10 h-10 rounded-lg text-sm font-medium transition-all ${
                    currentPage === pageNum
                      ? 'bg-blue-500 text-white'
                      : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 disabled:opacity-30 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
          >
            <FiChevronRight size={20} />
          </button>
        </div>
      )}

      {/* Direct Attendance Modal */}
      {attendanceModal.open && attendanceModal.employee && (
        <DirectAttendanceModal
          employee={attendanceModal.employee}
          type={attendanceModal.type}
          date={selectedDate}
          onClose={() => setAttendanceModal({ open: false, employee: null, type: null })}
          onSubmit={(data) => handleDirectAttendance(attendanceModal.type, attendanceModal.employee, data)}
          loading={attendanceLoading}
        />
      )}
    </div>
  );
};

export default DirectAttendance;